import {
  clientToServer
} from "./chunk-M7DP4AYQ.js";
import {
  AbstractQuery,
  ExpressionBuilder,
  assert,
  astSchema,
  defaultFormat,
  jsonSchema,
  mapCondition,
  newQuery,
  newQuerySymbol,
  readonly,
  staticParam,
  toStaticParam,
  valita_exports
} from "./chunk-CESYRWV4.js";

// ../zero-protocol/src/custom-queries.ts
var transformRequestBodySchema = valita_exports.array(
  valita_exports.object({
    id: valita_exports.string(),
    name: valita_exports.string(),
    args: readonly(valita_exports.array(jsonSchema))
  })
);
var transformedQuerySchema = valita_exports.object({
  id: valita_exports.string(),
  name: valita_exports.string(),
  ast: astSchema
});
var erroredQuerySchema = valita_exports.object({
  error: valita_exports.literal("app"),
  id: valita_exports.string(),
  name: valita_exports.string(),
  details: jsonSchema
});
var transformResponseBodySchema = valita_exports.array(
  valita_exports.union(transformedQuerySchema, erroredQuerySchema)
);
var transformRequestMessageSchema = valita_exports.tuple([
  valita_exports.literal("transform"),
  transformRequestBodySchema
]);
var transformResponseMessageSchema = valita_exports.tuple([
  valita_exports.literal("transformed"),
  transformResponseBodySchema
]);

// ../zero-schema/src/builder/relationship-builder.ts
function relationships(table2, cb) {
  const relationships2 = cb({ many, one });
  return {
    name: table2.schema.name,
    relationships: relationships2
  };
}
function many(...args) {
  return args.map((arg) => ({
    sourceField: arg.sourceField,
    destField: arg.destField,
    destSchema: arg.destSchema.schema.name,
    cardinality: "many"
  }));
}
function one(...args) {
  return args.map((arg) => ({
    sourceField: arg.sourceField,
    destField: arg.destField,
    destSchema: arg.destSchema.schema.name,
    cardinality: "one"
  }));
}

// ../zql/src/query/static-query.ts
var StaticQuery = class _StaticQuery extends AbstractQuery {
  constructor(schema, tableName, ast, format, system = "permissions", customQueryID, currentJunction) {
    super(
      void 0,
      schema,
      tableName,
      ast,
      format,
      system,
      customQueryID,
      currentJunction
    );
  }
  expressionBuilder() {
    return new ExpressionBuilder(this._exists);
  }
  [newQuerySymbol](_delegate, schema, tableName, ast, format, customQueryID, currentJunction) {
    return new _StaticQuery(
      schema,
      tableName,
      ast,
      format,
      "permissions",
      customQueryID,
      currentJunction
    );
  }
  get ast() {
    return this._completeAst();
  }
  materialize() {
    throw new Error("StaticQuery cannot be materialized");
  }
  run() {
    return Promise.reject(new Error("StaticQuery cannot be run"));
  }
  preload() {
    throw new Error("StaticQuery cannot be preloaded");
  }
};

// ../zero-schema/src/permissions.ts
var ANYONE_CAN = [
  (_, eb) => eb.and()
];
var ANYONE_CAN_DO_ANYTHING = {
  row: {
    select: ANYONE_CAN,
    insert: ANYONE_CAN,
    update: {
      preMutation: ANYONE_CAN,
      postMutation: ANYONE_CAN
    },
    delete: ANYONE_CAN
  }
};
var NOBODY_CAN = [];
async function definePermissions(schema, definer) {
  const expressionBuilders = {};
  for (const name of Object.keys(schema.tables)) {
    expressionBuilders[name] = new StaticQuery(
      schema,
      name,
      { table: name },
      defaultFormat
    ).expressionBuilder();
  }
  const config = await definer();
  return compilePermissions(schema, config, expressionBuilders);
}
function compilePermissions(schema, authz, expressionBuilders) {
  if (!authz) {
    return void 0;
  }
  const nameMapper = clientToServer(schema.tables);
  const ret = { tables: {} };
  for (const [tableName, tableConfig] of Object.entries(authz)) {
    const serverName = schema.tables[tableName].serverName ?? tableName;
    ret.tables[serverName] = {
      row: compileRowConfig(
        nameMapper,
        tableName,
        tableConfig.row,
        expressionBuilders[tableName]
      ),
      cell: compileCellConfig(
        nameMapper,
        tableName,
        tableConfig.cell,
        expressionBuilders[tableName]
      )
    };
  }
  return ret;
}
function compileRowConfig(clientToServer2, tableName, rowRules, expressionBuilder) {
  if (!rowRules) {
    return void 0;
  }
  return {
    select: compileRules(
      clientToServer2,
      tableName,
      rowRules.select,
      expressionBuilder
    ),
    insert: compileRules(
      clientToServer2,
      tableName,
      rowRules.insert,
      expressionBuilder
    ),
    update: {
      preMutation: compileRules(
        clientToServer2,
        tableName,
        rowRules.update?.preMutation,
        expressionBuilder
      ),
      postMutation: compileRules(
        clientToServer2,
        tableName,
        rowRules.update?.postMutation,
        expressionBuilder
      )
    },
    delete: compileRules(
      clientToServer2,
      tableName,
      rowRules.delete,
      expressionBuilder
    )
  };
}
function compileRules(clientToServer2, tableName, rules, expressionBuilder) {
  if (!rules) {
    return void 0;
  }
  return rules.map((rule) => {
    const cond = rule(authDataRef, expressionBuilder);
    return ["allow", mapCondition(cond, tableName, clientToServer2)];
  });
}
function compileCellConfig(clientToServer2, tableName, cellRules, expressionBuilder) {
  if (!cellRules) {
    return void 0;
  }
  const ret = {};
  for (const [columnName, rules] of Object.entries(cellRules)) {
    ret[columnName] = {
      select: compileRules(
        clientToServer2,
        tableName,
        rules.select,
        expressionBuilder
      ),
      insert: compileRules(
        clientToServer2,
        tableName,
        rules.insert,
        expressionBuilder
      ),
      update: {
        preMutation: compileRules(
          clientToServer2,
          tableName,
          rules.update?.preMutation,
          expressionBuilder
        ),
        postMutation: compileRules(
          clientToServer2,
          tableName,
          rules.update?.postMutation,
          expressionBuilder
        )
      },
      delete: compileRules(
        clientToServer2,
        tableName,
        rules.delete,
        expressionBuilder
      )
    };
  }
  return ret;
}
var CallTracker = class _CallTracker {
  #anchor;
  #path;
  constructor(anchor, path) {
    this.#anchor = anchor;
    this.#path = path;
  }
  get(target, prop) {
    if (prop === toStaticParam) {
      return target[toStaticParam];
    }
    assert(typeof prop === "string");
    const path = [...this.#path, prop];
    return new Proxy(
      {
        [toStaticParam]: () => staticParam(this.#anchor, path)
      },
      new _CallTracker(this.#anchor, path)
    );
  }
};
function baseTracker(anchor) {
  return new Proxy(
    {
      [toStaticParam]: () => {
        throw new Error("no JWT field specified");
      }
    },
    new CallTracker(anchor, [])
  );
}
var authDataRef = baseTracker("authData");
var preMutationRowRef = baseTracker("preMutationRow");

// ../zql/src/query/escape-like.ts
function escapeLike(val) {
  return val.replace(/[%_]/g, "\\$&");
}

// ../zql/src/query/named.ts
function querify(s) {
  return makeQueryBuilders(s);
}
function namedQuery(name, fn) {
  return (...args) => fn(...args).nameAndArgs(name, args);
}
function makeQueryBuilders(schema) {
  return new Proxy(
    {},
    {
      get: (target, prop) => {
        if (prop in target) {
          return target[prop];
        }
        if (!(prop in schema.tables)) {
          throw new Error(`Table ${prop} does not exist in schema`);
        }
        const q = newQuery(void 0, schema, prop);
        target[prop] = q;
        return q;
      }
    }
  );
}

export {
  transformRequestMessageSchema,
  transformResponseMessageSchema,
  relationships,
  ANYONE_CAN,
  ANYONE_CAN_DO_ANYTHING,
  NOBODY_CAN,
  definePermissions,
  escapeLike,
  querify,
  namedQuery
};
//# sourceMappingURL=chunk-DRKWTXPZ.js.map
